/**
 * Switch landing — the flag a customer switch parks for the shell it reloads
 * into.
 *
 * `switchCustomer` succeeds even when the target customer holds no employee
 * row for the caller: the token carries `roles: []` and every write then
 * fails with FORBIDDEN while the switcher reported success. The API now
 * says so on the mutation result (`targetRoleState`), but the switch is a
 * hard reload, so the verdict has to cross the reload somewhere. That
 * somewhere is sessionStorage: it survives the localStorage sweep and the
 * IndexedDB purge that `PlatformCustomerService.switchTo` performs, and it
 * dies with the tab, which is the right lifetime for a landing notice.
 *
 * Pure: no Angular, no storage. `SwitchLandingService` owns the reads and
 * writes; this file owns the shape and the parse, so both are held at 100 %
 * by the coverage gate.
 */

/** sessionStorage key the switch parks under and the shell reads on boot. */
export const SWITCH_LANDING_KEY = 'ws-switch-landing';

/** A parked flag older than this is stale — a tab left open overnight
 *  should not greet the next morning with yesterday's switch. */
export const SWITCH_LANDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * The role states the shell has something to say about. Both mean the same
 * thing to the person, "nothing here will save", and share the banner copy:
 *
 *   - `NO_EMPLOYEE_ROW` — the API's own enum name: no row under the target.
 *   - `NO_ROLES` — settings-side only: the API said `RESOLVED` (a row
 *     exists) but `targetRoleCount` was 0, e.g. a guest row the invite
 *     path made, or a row whose roles were removed. The same FORBIDDEN on
 *     the first save, so the same notice.
 *
 * A resolved switch that carries roles is the silent path and is never
 * parked.
 */
export type SwitchLandingRoleState = 'NO_EMPLOYEE_ROW' | 'NO_ROLES';

const LANDING_ROLE_STATES: ReadonlySet<string> = new Set<SwitchLandingRoleState>([
  'NO_EMPLOYEE_ROW',
  'NO_ROLES',
]);

/**
 * What, if anything, a switch result should park. `targetRoleState` and
 * `targetRoleCount` are the switchCustomer mutation's fields as returned;
 * an older API that sent neither parks nothing.
 */
export function landingRoleState(
  targetRoleState: string | null | undefined,
  targetRoleCount: number | null | undefined,
): SwitchLandingRoleState | null {
  if (targetRoleState === 'NO_EMPLOYEE_ROW') return 'NO_EMPLOYEE_ROW';
  if (targetRoleState === 'RESOLVED' && targetRoleCount === 0) return 'NO_ROLES';
  return null;
}

export interface SwitchLanding {
  /** The customer the session switched INTO. Compared with the session's
   *  `customer-id` on read, so a second switch never shows a stale notice. */
  customerId: string;
  /** For the banner copy. Empty when the switcher had no name to hand. */
  customerName: string;
  roleState: SwitchLandingRoleState;
  /** `targetRoleCount` from the mutation — 0 for a missing row. */
  roleCount: number;
  /** Epoch millis at park time — drives the max-age check. */
  at: number;
}

export function serialiseSwitchLanding(landing: SwitchLanding): string {
  return JSON.stringify(landing);
}

/**
 * Parse a stored flag. Null for anything that is not a well-formed, fresh,
 * recognised landing: malformed JSON, a non-object, a missing customer id,
 * a role state the shell does not know, or an entry older than `maxAgeMs`.
 * A parse that returns null is a signal to DROP the stored value.
 */
export function parseSwitchLanding(
  raw: string | null | undefined,
  now: number,
  maxAgeMs: number = SWITCH_LANDING_MAX_AGE_MS,
): SwitchLanding | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const record = parsed as Record<string, unknown>;

  const customerId = record['customerId'];
  if (typeof customerId !== 'string' || customerId.length === 0) return null;
  const roleState = record['roleState'];
  if (typeof roleState !== 'string' || !LANDING_ROLE_STATES.has(roleState)) return null;

  const at = record['at'];
  if (typeof at !== 'number' || !Number.isFinite(at)) return null;
  if (now - at > maxAgeMs) return null;

  const roleCount = record['roleCount'];
  const customerName = record['customerName'];
  return {
    customerId,
    customerName: typeof customerName === 'string' ? customerName : '',
    roleState: roleState as SwitchLandingRoleState,
    roleCount: typeof roleCount === 'number' && Number.isFinite(roleCount) ? roleCount : 0,
    at,
  };
}
